/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Problems (strategy 9.6, mockup admin-10, "Build as drawn"): the problem files from GET /v1/admin/pulse/problems,
 * filtered by status and domain and sorted by impact; a row's drawer with the whole file: symptoms, the evidence (each a
 * link), the likely cause, the suggested fix and what would prove it, the status actions (Investigating, Fixed in… asking
 * for a version, Duplicate of…, Won't fix, Verified), notes, the verification block and The Lab line. `?problem=` opens
 * one. Status changes and notes are admin actions, audited by the service.
 */
import { useState } from 'react';
import { Bug, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import {
  OPEN_PROBLEM_STATUSES, PROBLEM_ACTION_LABELS, PROBLEM_STATUS_CHIPS, domainLabel, evidenceText, healthChip, labLineText, openedByText, pctText, problemImpact, problemStatusActions,
  problemStatusChip, pulseExportHref, pulseHref, sortBy, subjectName, symptomsText, toggleSort, topErrorsText, whenText,
  type PulseDomain, type PulseProblem, type PulseProblemStatus, type SortState,
} from '../../lib/pulse';
import { SaveNote } from '../../components/SaveNote';
import { Button, ConfirmDialog, Dialog, Drawer, EmptyState, Field, Input, PageHeader, Panel, Select, Skeleton, Stack, StatusChip, Table, Textarea, type Column } from '../../ui';
import { count } from '../../ui/format';
import { Chip, DomainSelect, ExportButton, Facts, PageLink, PulseError, useLoad, usePagedList, useQueryParam } from './common';

const STATUS_FILTERS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'open', label: 'Status: Open' }, { value: '', label: 'Status: All' },
  ...(Object.keys(PROBLEM_STATUS_CHIPS) as PulseProblemStatus[]).map((status) => ({ value: status, label: PROBLEM_ACTION_LABELS[status].replace(/…$/, '') })),
];
const EVIDENCE_SHOWN = 8;

export function PulseProblems({ canEdit }: { canEdit: boolean }) {
  const [status, setStatus] = useState('open');
  const [domain, setDomain] = useState('');
  const [sort, setSort] = useState<SortState>({ key: 'impact', direction: 'desc' });
  const [selectedId, setSelectedId] = useQueryParam('problem');
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  // "Open" is four statuses, so it is filtered here; a single status goes to the service.
  const params = { status: status && status !== 'open' ? status : undefined, domain: domain || undefined, sort: '-impact' };
  const list = usePagedList((cursor) => api.pulseProblems({ ...params, cursor, limit: 100 }), JSON.stringify(params));
  const visible = status === 'open' ? list.items.filter((problem) => OPEN_PROBLEM_STATUSES.includes(problem.status)) : list.items;
  const rows = sortBy(visible, sort, (problem, key) => {
    switch (key) {
      case 'title': return problem.title;
      case 'domain': return domainLabel(problem.domain);
      case 'status': return problem.status;
      case 'asks': return problem.symptoms.asks;
      case 'firstSeen': return problem.symptoms.firstSeen;
      case 'lastSeen': return problem.symptoms.lastSeen;
      case 'assignee': return problem.assignee ?? '';
      default: return problemImpact(problem);
    }
  });
  const selected = list.items.find((problem) => problem.problemId === selectedId) ?? null;
  const newToday = list.items.filter((problem) => whenText(problem.openedAt).startsWith('Today')).length;

  const columns: Array<Column<PulseProblem>> = [
    { key: 'title', header: 'Title', sortable: true, cell: (problem) => problem.title },
    { key: 'domain', header: 'Domain', sortable: true, hideOnPhone: true, cell: (problem) => domainLabel(problem.domain) },
    { key: 'status', header: 'Status', sortable: true, cell: (problem) => <Chip spec={problemStatusChip(problem)} /> },
    { key: 'asks', header: 'Asks affected', numeric: true, sortable: true, cell: (problem) => count(problem.symptoms.asks) },
    { key: 'impact', header: 'Impact', numeric: true, sortable: true, hideOnPhone: true, cell: (problem) => count(problemImpact(problem)) },
    { key: 'firstSeen', header: 'First seen', numeric: true, sortable: true, hideOnPhone: true, cell: (problem) => whenText(problem.symptoms.firstSeen) },
    { key: 'lastSeen', header: 'Last seen', numeric: true, sortable: true, cell: (problem) => whenText(problem.symptoms.lastSeen) },
    { key: 'assignee', header: 'Assignee', sortable: true, muted: true, hideOnPhone: true, cell: (problem) => problem.assignee ?? '—' },
  ];

  const replace = (problem: PulseProblem) => {
    if (list.items.some((row) => row.problemId === problem.problemId)) list.setItems((items) => items.map((row) => (row.problemId === problem.problemId ? problem : row)));
    else list.prepend(problem);
  };

  return (
    <>
      <PageHeader
        meta={<span>Recurring problems the analyst opened, with the evidence behind each.{list.load.state === 'ready' ? ` ${count(visible.length)} ${status === 'open' ? 'open' : 'shown'}, ${count(newToday)} new today.` : ''}</span>}
        actions={(
          <>
            <Select size="sm" className="ui-w-auto" aria-label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
              {STATUS_FILTERS.map((option) => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
            </Select>
            <DomainSelect value={domain} onChange={setDomain} />
            <ExportButton href={pulseExportHref('problems', params)} />
            {canEdit ? <Button variant="primary" size="sm" icon={Plus} onClick={() => setCreating(true)}>New problem</Button> : null}
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          <Panel flush title="Problem files" description="Sorted by impact: asks affected × the share that failed">
            {list.load.state === 'failed' ? <div className="ui-panel-pad"><PulseError title="The problems could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /></div> : (
              <Table
                label="Problem files"
                columns={columns}
                rows={rows}
                rowKey={(problem) => problem.problemId}
                loading={list.load.state === 'loading'}
                skeletonRows={5}
                selectedKey={selectedId}
                onRowClick={(problem) => setSelectedId(problem.problemId, { replace: false })}
                sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSort(toggleSort(sort, key, !['title', 'domain', 'status', 'assignee'].includes(key))) }}
                more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
                empty={<EmptyState compact icon={Bug} title={status === 'open' ? 'No open problems' : 'No problems match'}>{status === 'open' ? 'The analyst opens a file when an intent keeps failing or a tool starts failing; an admin can open one too.' : 'Try another status or domain.'}</EmptyState>}
              />
            )}
            <div className="ui-panel-pad ui-panel-pad--top ui-panel-pad--note"><SaveNote note={note} /></div>
          </Panel>
        </Stack>
      </div>
      <ProblemDrawer problem={selected} pendingId={selectedId && !selected ? selectedId : null} canEdit={canEdit} onClose={() => setSelectedId(null)} onChanged={replace} onNote={setNote} />
      {canEdit ? <NewProblemDialog open={creating} domain={domain} onCancel={() => setCreating(false)} onCreated={(problem) => { replace(problem); setCreating(false); setNote({ ok: true, text: `${problem.problemId} opened.` }); setSelectedId(problem.problemId, { replace: false }); }} /> : null}
    </>
  );
}

interface ProblemDrawerProps {
  problem: PulseProblem | null;
  pendingId: string | null;
  canEdit: boolean;
  onClose: () => void;
  onChanged: (problem: PulseProblem) => void;
  onNote: (note: { ok: boolean; text: string }) => void;
}

/** The whole file: facts, symptoms, evidence links, the analyst's cause and fix, status actions, notes, verification, The Lab. */
function ProblemDrawer({ problem, pendingId, canEdit, onClose, onChanged, onNote }: ProblemDrawerProps) {
  const [ask, setAsk] = useState<'fixed-in' | 'duplicate-of' | 'wont-fix' | null>(null);
  const [value, setValue] = useState('');
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A `?problem=` not on the loaded page is read on its own.
  const direct = useLoad<PulseProblem | null>(() => (pendingId ? api.pulseProblem(pendingId) : Promise.resolve(null)), pendingId ?? '');
  const file = problem ?? (direct.load.state === 'ready' ? direct.load.data : null);
  const labEnabled = useLoad(() => api.pulseSettings().then((view) => view.settings.lab.enabled), 'lab');

  const setStatus = async (status: PulseProblemStatus, extra: { version?: string; duplicateOf?: string } = {}) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.setPulseProblemStatus(file.problemId, { status, ...extra });
      onChanged(result.problem);
      onNote({ ok: true, text: `${file.problemId} is now ${problemStatusChip(result.problem).label}. The change is in the audit trail.` });
      setAsk(null);
      setValue('');
    } catch (failure) {
      setError(failure instanceof Error && failure.message ? failure.message : 'The service refused the change.');
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!file || !noteText.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.addPulseProblemNote(file.problemId, noteText.trim());
      onChanged(result.problem);
      setNoteText('');
    } catch (failure) {
      setError(failure instanceof Error && failure.message ? failure.message : 'The service refused the note.');
    } finally {
      setBusy(false);
    }
  };

  const onAction = (status: PulseProblemStatus) => {
    if (status === 'fixed-in' || status === 'duplicate-of' || status === 'wont-fix') {
      setValue('');
      setAsk(status);
      return;
    }
    void setStatus(status);
  };

  const evidenceOf = (kind: 'record' | 'feedback' | 'health') => (file?.evidence ?? []).filter((item) => item.kind === kind);
  const evidenceHref = (kind: 'record' | 'feedback' | 'health', id: string) => (kind === 'record' ? pulseHref.record(id) : kind === 'feedback' ? pulseHref.feedback(id) : pulseHref.subject(id));

  return (
    <>
      <Drawer
        open={problem !== null || pendingId !== null}
        onClose={onClose}
        title={file ? `${file.problemId} · ${file.title}` : 'Problem'}
        description={file ? `${domainLabel(file.domain)} · opened by ${openedByText(file.openedBy)} · ${whenText(file.openedAt)}` : undefined}
        className="ui-drawer--wide"
        footer={file ? <p className="pulse-foot">{labLineText(file.lab, labEnabled.load.state === 'ready' ? labEnabled.load.data : true)}{file.lab.gheIssueUrl ? <> <a href={file.lab.gheIssueUrl} target="_blank" rel="noreferrer">Open the GHE issue</a></> : null}</p> : undefined}
      >
        {!file && direct.load.state === 'loading' ? <div className="ui-form" aria-busy="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="xl" /></div> : null}
        {!file && direct.load.state === 'failed' ? <PulseError title="The problem could not be loaded" error={direct.load.error} onRetry={direct.reload} retrying={direct.reloading} /> : null}
        {file ? (
          <div className="ui-form">
            <Facts rows={[
              ['Opened by', `${openedByText(file.openedBy)} · ${whenText(file.openedAt)}`],
              ['Status', <span className="ui-chips"><Chip spec={problemStatusChip(file)} />{file.assignee ? <span className="ui-quiet">assigned to {file.assignee}</span> : null}</span>],
              ['Subjects', file.subjectIds.length ? (
                <span className="ui-chips">{file.subjectIds.map((id) => <span key={id}><PageLink href={pulseHref.subject(id)}><code>{subjectName(id).name}</code></PageLink> {file.subjectStates?.[id] ? <Chip spec={healthChip(file.subjectStates[id])} /> : null}</span>)}</span>
              ) : 'none'],
              ['Intents', file.intentKeys.length ? <span className="pulse-links">{file.intentKeys.map((key) => <PageLink key={key} href={pulseHref.intent(key)}>{key.replace(/-/g, ' ')}</PageLink>)}</span> : 'none'],
              ['Symptoms', <span className="ui-cell-stack"><span>{symptomsText(file.symptoms)}</span>{file.symptoms.topErrors?.length ? <span className="ui-cell-sub">{topErrorsText(file.symptoms.topErrors)}</span> : null}</span>],
              ['Evidence', (
                <span className="ui-cell-stack">
                  <span>{evidenceText(file.evidence)}</span>
                  {(['record', 'feedback', 'health'] as const).map((kind) => {
                    const items = evidenceOf(kind);
                    if (!items.length) return null;
                    return (
                      <span key={kind} className="pulse-links">
                        {items.slice(0, EVIDENCE_SHOWN).map((item) => <PageLink key={item.id} href={evidenceHref(kind, item.id)}>{kind === 'health' ? subjectName(item.id).name : item.id}</PageLink>)}
                        {items.length > EVIDENCE_SHOWN ? <span className="ui-quiet">+{count(items.length - EVIDENCE_SHOWN)} more</span> : null}
                      </span>
                    );
                  })}
                </span>
              )],
            ]} />
            {file.likelyCause || file.suggestedFix || file.wouldProve ? (
              <div className="pulse-analysis">
                {file.likelyCause ? <p><strong>Likely cause</strong>{` (analyst${file.codeArea ? `, ${file.codeArea}` : ''}): ${file.likelyCause}`}</p> : null}
                {file.suggestedFix ? <p><strong>Suggested fix:</strong>{` ${file.suggestedFix}`}{file.wouldProve ? <> <strong>Would prove it:</strong>{` ${file.wouldProve}`}</> : null}</p> : null}
                {!file.suggestedFix && file.wouldProve ? <p><strong>Would prove it:</strong>{` ${file.wouldProve}`}</p> : null}
              </div>
            ) : <p className="ui-para ui-quiet">The analyst has not written a cause or a fix for this file yet.</p>}
            {file.verification ? (
              <div className="ui-section">
                <h3 className="ui-section-title">Fix verification</h3>
                <p className="ui-para">
                  Watching from {whenText(file.verification.startedAt)} to {whenText(file.verification.endsAt)}. Before: {count(file.verification.before.asks)} asks, {pctText(file.verification.before.failPct)} failed, {count(file.verification.before.undo)} undone.
                  {file.verification.after ? <> After: {count(file.verification.after.asks)} asks, {pctText(file.verification.after.failPct)} failed, {count(file.verification.after.undo)} undone.</> : ' After: not yet measured.'}
                  {file.verification.verdict ? <> Verdict: <StatusChip tone={file.verification.verdict === 'verified' ? 'ok' : 'bad'}>{file.verification.verdict === 'verified' ? 'verified' : 'not fixed'}</StatusChip></> : null}
                </p>
              </div>
            ) : null}
            {canEdit ? (
              <div className="pulse-actions" aria-label="Status actions">
                {problemStatusActions(file.status).map((status, index) => (
                  <Button key={status} size="sm" variant={index === 0 ? 'primary' : status === 'wont-fix' ? 'danger' : 'secondary'} disabled={busy} onClick={() => onAction(status)}>{PROBLEM_ACTION_LABELS[status]}</Button>
                ))}
              </div>
            ) : null}
            <div className="ui-section">
              <h3 className="ui-section-title">Notes</h3>
              {file.notes.length ? (
                <ul className="ui-list">{file.notes.map((item, index) => <li key={index}><span className="ui-quiet">{whenText(item.at)} · {item.by}:</span> {item.text}</li>)}</ul>
              ) : <p className="ui-para ui-quiet">No notes yet.</p>}
              {canEdit ? (
                <div className="ui-form">
                  <Field label="Add a note" labelHidden>
                    <Textarea rows={2} maxLength={2000} placeholder="Add a note…" value={noteText} onChange={(event) => setNoteText(event.target.value)} />
                  </Field>
                  <div className="ui-actions"><Button size="sm" busy={busy} disabled={!noteText.trim()} onClick={() => void addNote()}>Add note</Button></div>
                </div>
              ) : null}
            </div>
            {error && !ask ? <SaveNote note={{ ok: false, text: error }} /> : null}
          </div>
        ) : null}
      </Drawer>

      <Dialog
        open={ask === 'fixed-in' || ask === 'duplicate-of'}
        onClose={() => setAsk(null)}
        title={ask === 'fixed-in' ? 'Fixed in which version?' : 'Duplicate of which problem?'}
        description={ask === 'fixed-in' ? 'The verification watch starts when the deploy ledger says this version is live, and compares the 7 days (or 50 asks) after it with the time before.' : 'This file closes as a duplicate; its evidence stays here.'}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setAsk(null)} disabled={busy}>Cancel</Button>
            <Button variant="primary" busy={busy} disabled={!value.trim()} onClick={() => void setStatus(ask as PulseProblemStatus, ask === 'fixed-in' ? { version: value.trim() } : { duplicateOf: value.trim() })}>{ask === 'fixed-in' ? 'Mark fixed in' : 'Mark duplicate'}</Button>
          </>
        )}
      >
        <div className="ui-form">
          <Field label={ask === 'fixed-in' ? 'Version' : 'Problem id'} help={ask === 'fixed-in' ? 'The release tag or version number, such as v2026.10.24.1 or 0.4.4.3.' : 'Such as P-0142.'}>
            <Input value={value} onChange={(event) => setValue(event.target.value)} autoFocus />
          </Field>
          {error ? <SaveNote note={{ ok: false, text: error }} /> : null}
        </div>
      </Dialog>

      <ConfirmDialog
        open={ask === 'wont-fix'}
        title={file ? `Close ${file.problemId} as won't fix?` : "Won't fix?"}
        confirmLabel="Won't fix"
        busy={busy}
        error={error}
        onConfirm={() => void setStatus('wont-fix')}
        onCancel={() => setAsk(null)}
      >
        The file closes without a fix. Its evidence and notes stay, and it can be reopened as Investigating. The change is audited and sent to The Lab.
      </ConfirmDialog>
    </>
  );
}

/** New problem: a title and a domain; the analyst fills the rest as evidence arrives. */
function NewProblemDialog({ open, domain, onCancel, onCreated }: { open: boolean; domain: string; onCancel: () => void; onCreated: (problem: PulseProblem) => void }) {
  const [title, setTitle] = useState('');
  const [pick, setPick] = useState<string>(domain || 'gyro-ai-for-revit');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.createPulseProblem({ title: title.trim(), domain: pick as PulseDomain });
      onCreated(result.problem);
      setTitle('');
    } catch (failure) {
      setError(failure instanceof Error && failure.message ? failure.message : 'The service refused the problem.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title="New problem"
      description="A file an admin opens by hand; evidence is added from Feedback and Asks."
      footer={(
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!title.trim()} onClick={() => void create()}>Open the problem</Button>
        </>
      )}
    >
      <div className="ui-form">
        <Field label="Title" help="What is going wrong, in one line (up to 120 characters).">
          <Input value={title} maxLength={120} onChange={(event) => setTitle(event.target.value)} autoFocus />
        </Field>
        <Field label="Domain">
          <DomainSelect value={pick} all={false} size="md" onChange={setPick} />
        </Field>
        {error ? <SaveNote note={{ ok: false, text: error }} /> : null}
      </div>
    </Dialog>
  );
}
